"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, GitBranch, Users, CheckCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Products } from "src/types/productsTypes";
import { cn } from "src/lib/utils";

// Client component for animated cards
export default function FeatureCards({ section }: { section: Products['atlaside']['features']['cards'] }){
    const containerRef = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    // Transform scroll progress to position changes
    const card1X = useTransform(scrollYProgress, [0, 1], [0, 50]);
    const card1Y = useTransform(scrollYProgress, [0, 1], [0, 10]);
    const card2X = useTransform(scrollYProgress, [0, 1], [0, -10]);
    const card2Y = useTransform(scrollYProgress, [0, 1], [0, 50]);
    const card3X = useTransform(scrollYProgress, [0, 1], [0, 30]);
    const card3Y = useTransform(scrollYProgress, [0, 1], [0, -40]);

    const renderIcon = useMemo(() => ({
        gitBranch: <GitBranch className="w-6 h-6 text-primary-600" />,
        sparkles: <Sparkles className="w-6 h-6 text-primary-600" />,
        users: <Users className="w-6 h-6 text-primary-600" />,
    } as Record<string, React.ReactNode>), []);

    // Responsive breakpoint check for enabling absolute positioning/animations only on large screens
    const [isLargeScreen, setIsLargeScreen] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mediaQuery = window.matchMedia('(min-width: 1024px)');
        const update = () => setIsLargeScreen(mediaQuery.matches);
        update();
        mediaQuery.addEventListener('change', update);
        return () => mediaQuery.removeEventListener('change', update);
    }, []);

    const cardWidth = "lg:w-[470px] xl:w-[590px] w-full";

    return (
        <div
            ref={containerRef}
            className="relative z-10 h-auto lg:h-[560px] xl:h-[500px] space-y-6 lg:space-y-0"
        >
            {section.map((card, idx) => {
                const cardConfigs = [
                    {
                        className: cn("lg:absolute lg:top-0 lg:left-0", cardWidth),
                        style: { x: card1X, y: card1Y },
                        delay: 0,
                    },
                    {
                        className: cn("lg:absolute lg:top-1/2 lg:-translate-y-[60%] xl:-translate-y-[70%] lg:translate-x-[10px] lg:right-0", cardWidth),
                        style: { x: card2X, y: card2Y },
                        delay: 0.2,
                    },
                    {
                        className: cn("lg:absolute lg:top-0 lg:translate-y-[140%] xl:translate-y-[150%] lg:left-0 lg:-translate-x-[1%] xl:-translate-x-[3%]", cardWidth),
                        style: { x: card3X, y: card3Y },
                        delay: 0.4,
                    },
                ];

                const cfg = cardConfigs[idx];
                if (!cfg) return null;

                return (
                    <motion.div
                        key={idx}
                        className={cfg.className}
                        style={isLargeScreen ? cfg.style : { x: 0, y: 0 }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: cfg.delay }}
                    >
                        <Card className="shadow-none bg-white/90 text-left py-2">
                            <CardContent className="p-6">
                                <div className="size-11 iconBudg mb-3">
                                    {renderIcon[card.icon]}
                                </div>
                                <h3
                                    className={cn("text-lg text-bg font-bold", card.type === 'list' ? 'mb-4' : 'mb-2')}
                                >
                                    {card.title}
                                </h3>
                                {card.type === 'paragraph' ? (
                                    <p className="text-base text-muted-foreground">{card.description}</p>
                                ) : (
                                    <ul className="flex flex-col gap-4 text-base text-muted-foreground">
                                        {card.items.map((text: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <CheckCircle className="size-4 min-w-4 mt-1 text-primary-600" />
                                                {text}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                );
            })}
        </div>
    );
};